import React, { useEffect, useState } from 'react';
import { Timer, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { backendUrl } from './gameplanUtils';

const WINDOWS = [[30, '30 days'], [90, '90 days'], [365, '12 months']];
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

/* Average lodged → approved turnaround, shown at the top of the Approvals tab. */
export const TurnaroundStrip = ({ authToken, refreshSignal }) => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    fetch(`${backendUrl}/api/gameplan/approvals/turnaround?days=${days}`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => r.json()).then((d) => live && setData(d)).catch(() => {});
    return () => { live = false; };
  }, [authToken, days, refreshSignal]);

  const delta = data?.avg_bd != null && data?.prev?.avg_bd != null ? Math.round((data.avg_bd - data.prev.avg_bd) * 10) / 10 : null;
  const Trend = delta == null ? Minus : delta < 0 ? TrendingDown : delta > 0 ? TrendingUp : Minus;
  const fastest = data?.by_lender?.[0];
  const slowest = data?.by_lender?.length > 1 ? data.by_lender[data.by_lender.length - 1] : null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white px-5 py-4" data-testid="approvals-turnaround">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[#171B21] text-white grid place-items-center shrink-0"><Timer className="w-5 h-5" /></div>
          <div>
            <div className="text-[11px] font-black uppercase tracking-wider text-slate-500">Average turnaround · lodged → approved</div>
            <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
              <span className="text-3xl font-black tabular-nums text-slate-900" data-testid="turnaround-avg">{data ? (data.avg_bd != null ? data.avg_bd : '—') : '…'}</span>
              <span className="text-sm font-semibold text-slate-600">business days</span>
              {data?.avg_cal != null && <span className="text-xs text-slate-500 tabular-nums">({data.avg_cal} calendar)</span>}
              {delta != null && (
                <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums', delta < 0 ? 'bg-emerald-50 text-emerald-700' : delta > 0 ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600')} data-testid="turnaround-delta">
                  <Trend className="w-3.5 h-3.5" />{delta === 0 ? 'No change' : `${delta > 0 ? '+' : ''}${delta}d vs prior ${days}d`}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-xs text-slate-500 tabular-nums" data-testid="turnaround-sub">
              {data ? (data.count ? <>Median {data.median_bd}d · fastest {data.fastest_bd}d · slowest {data.slowest_bd}d · {data.within_5} of {data.count} within 5 days · based on {plural(data.count, 'approval')} in the last {days} days</> : `No approvals with a lodged date in the last ${days} days.`) : 'Loading…'}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
          {fastest && fastest.avg_bd != null && <span className="rounded-full bg-emerald-50 text-emerald-800 px-3 py-1 text-xs font-semibold tabular-nums" data-testid="turnaround-fastest">Fastest: {fastest.lender} · {fastest.avg_bd}d ({fastest.count})</span>}
          {slowest && slowest.avg_bd != null && <span className="rounded-full bg-amber-50 text-amber-800 px-3 py-1 text-xs font-semibold tabular-nums" data-testid="turnaround-slowest">Slowest: {slowest.lender} · {slowest.avg_bd}d ({slowest.count})</span>}
          <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50" role="tablist" aria-label="Turnaround window">
            {WINDOWS.map(([d, l]) => (
              <button key={d} type="button" role="tab" aria-selected={days === d} onClick={() => setDays(d)} data-testid={`turnaround-window-${d}`}
                className={cn('rounded-full px-3 py-1 text-xs font-bold transition-colors', days === d ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900')}>{l}</button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
